"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { NotificationBell } from "./NotificationBell";
import { useNotifications } from "./NotificationManager";
import { 
  Users,
  Clock,
  CheckCircle,
  AlertTriangle,
  Eye,
  Download,
  Calendar,
  Filter,
  Search,
  MoreHorizontal,
  ArrowLeft,
  Building2,
  UserCheck,
  Mail
} from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

interface HRDashboardProps {
  onBack: () => void;
  onShowTerms?: () => void;
}

// Mock data for pending candidates
const mockPendingCandidates = [
  {
    id: 'cand_002',
    name: 'Mike Chen',
    email: 'mike.chen@email.com',
    role: 'Senior Frontend Developer',
    joinerType: 'employee',
    department: 'Engineering',
    submittedAt: '2024-01-18T10:30:00Z',
    lastActivity: '2024-01-18T11:15:00Z',
    documentsCount: 8,
    priority: 'high' as const,
    avatar: '🧑‍💻'
  },
  {
    id: 'cand_004',
    name: 'Lisa Rodriguez',
    email: 'lisa.rodriguez@email.com',
    role: 'Product Marketing Manager',
    joinerType: 'employee',
    department: 'Marketing',
    submittedAt: '2024-01-18T14:20:00Z',
    lastActivity: '2024-01-18T14:45:00Z',
    documentsCount: 6,
    priority: 'medium' as const,
    avatar: '👩‍💼'
  },
  {
    id: 'cand_005',
    name: 'James Thompson',
    email: 'james.thompson@email.com',
    role: 'UX Research Intern',
    joinerType: 'intern',
    department: 'Design',
    submittedAt: '2024-01-19T09:15:00Z',
    lastActivity: '2024-01-19T09:30:00Z',
    documentsCount: 4,
    priority: 'low' as const,
    avatar: '🎨'
  }
];

export function HRDashboard({ onBack, onShowTerms }: HRDashboardProps) {
  const { notifications, unreadCount } = useNotifications('hr_team', 'hr');
  const [searchTerm, setSearchTerm] = React.useState('');

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Less than 1h ago';
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const stats = [
    {
      title: 'Pending Reviews',
      value: mockPendingCandidates.length,
      icon: Clock,
      color: 'bg-orange-100 text-orange-700',
      trend: '+2 from yesterday'
    },
    {
      title: 'Completed Today',
      value: 12,
      icon: CheckCircle,
      color: 'bg-green-100 text-green-700',
      trend: '+4 from yesterday'
    },
    {
      title: 'Active Candidates',
      value: 28,
      icon: Users,
      color: 'bg-blue-100 text-blue-700',
      trend: '+6 this week'
    },
    {
      title: 'Urgent Actions',
      value: unreadCount,
      icon: AlertTriangle,
      color: 'bg-red-100 text-red-700',
      trend: 'Needs attention'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex flex-col">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/70 backdrop-blur-sm border-b border-gray-200/50 p-6"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-gray-700 hover:text-gray-900 hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Demo
            </Button>
            <div className="h-6 w-px bg-gray-300" />
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
          </div>

          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2 text-gray-700">
              <Building2 className="w-5 h-5 text-blue-600" />
              <span>HR Dashboard</span>
            </div>
            <NotificationBell userId="hr_team" userType="hr" />
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-gray-900 mb-2">HR Onboarding Dashboard</h1>
            <p className="text-gray-600">
              Manage candidate onboarding, review submissions, and track progress.
            </p>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 + index * 0.05 }}
              >
                <Card className="p-6 bg-white/80 backdrop-blur-sm border border-white/20 hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm">{stat.title}</p>
                      <p className="text-2xl text-gray-900 mt-1">{stat.value}</p>
                      <p className="text-xs text-gray-500 mt-1">{stat.trend}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                      <stat.icon className="w-6 h-6" />
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Urgent Notifications Banner */}
          {unreadCount > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-red-50 border border-red-200 p-4">
                <div className="flex items-center space-x-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-red-900 text-sm">
                      {unreadCount} urgent notification{unreadCount !== 1 ? 's' : ''} requiring attention
                    </h3>
                    <p className="text-red-700 text-xs mt-1">
                      Candidates have been waiting for review. Click the notification bell to see details.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    View All
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Pending Reviews Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-gray-900">Pending Reviews</h2>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search candidates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <Button variant="outline" size="sm">
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                </Button>
              </div>
            </div>

            {/* Candidates List */}
            <div className="space-y-4">
              {mockPendingCandidates.map((candidate, index) => (
                <motion.div
                  key={candidate.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <Card className="p-6 bg-white/80 backdrop-blur-sm border border-white/20 hover:shadow-lg transition-all duration-300 hover:border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {/* Avatar */}
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-xl">
                          {candidate.avatar}
                        </div>

                        {/* Candidate Info */}
                        <div>
                          <div className="flex items-center space-x-3">
                            <h3 className="text-gray-900">{candidate.name}</h3>
                            <Badge className={`text-xs ${getPriorityColor(candidate.priority)}`}>
                              {candidate.priority} priority
                            </Badge>
                          </div>
                          <p className="text-gray-600 text-sm mt-1">{candidate.role}</p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center space-x-1">
                              <Building2 className="w-3 h-3" />
                              <span>{candidate.department}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <UserCheck className="w-3 h-3" />
                              <span className="capitalize">{candidate.joinerType}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <Clock className="w-3 h-3" />
                              <span>Submitted {getTimeAgo(candidate.submittedAt)}</span>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-3">
                        <div className="text-right text-sm">
                          <p className="text-gray-600">{candidate.documentsCount} documents</p>
                          <p className="text-xs text-gray-500">Ready for review</p>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Review
                          </Button>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-600 hover:text-gray-800"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Contact
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Empty State */}
            {mockPendingCandidates.length === 0 && (
              <Card className="p-12 text-center bg-white/80 backdrop-blur-sm border border-white/20">
                <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-gray-900 mb-2">All caught up!</h3>
                <p className="text-gray-600 text-sm">
                  No pending reviews at the moment. New candidate submissions will appear here.
                </p>
              </Card>
            )}
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="border-t border-gray-200/50 bg-white/50 backdrop-blur-sm p-6"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>HR Portal</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-blue-600 hover:text-blue-700 hover:underline transition-colors"
            >
              Terms &amp; Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}